/* eslint-disable no-nested-ternary */
import { generatePath } from 'react-router-dom';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ChangeLog,
  Pagination,
  Response,
  TransactionWarning,
  TransactionWarningActionEnum,
} from '@ui/src';
import {
  FormValues,
  NewApplicationStatus,
  Match,
  NewApplicationListItem,
  NewApplicationIndividualStepsStatuses,
  SystemLogs,
  ApplicationComment,
  ApplicationDocument,
  ApplicationAgreement,
  DictionaryItem,
  AccountsCurrencies,
  AccountsFlows,
  ExpectedFundsOrigins,
  CRSAndFatca,
  ApplicationDocumentStatus,
  AllowedCategory,
} from '../../types';
import updateApplicationValues from '../helpers/update-application-values';
import { COFAndIndividualQuestionnarieFormValues } from '../../../individual/types';
import { applicationsApi } from '../../store/services';
import { clientsDocumentsApi } from '../../../../store/documents/services';
import { DocumentComment } from '../../../common/documents/types';
import {
  CustomerScoreUpdateStatus,
  CustomerScore,
  CustomerScoreQueuedProps,
  Questionnarie,
  OngoingMonitring,
  OngoingCheckingStatus,
} from '../../../common/types';

import ROUTES from '../../../../routes';

export interface NewApplicationIndividualInfoState {
  isLoading: boolean;
  applicationId?: string;
  newChatMessageRecieved?: boolean;
  chatMessageHintIsVisible?: boolean;
  requestInProgress: boolean;
  formValues?: FormValues;
  selectedDocumentsCounts: number;
  documents?: ApplicationDocument[];
  newApplicationInfo?: NewApplicationListItem;
  agreements?: ApplicationAgreement[];
  agreementsErrorsList?: string;
  deleteSuccess?: boolean;
  hintVisible?: boolean;
  stepsStatus?: NewApplicationIndividualStepsStatuses;
  systemLogs?: SystemLogs[];
  documentId?: string;
  document?: ApplicationDocument;
  ongoingMonitoring?: OngoingMonitring[];
  ongoingCheckingStatus?: OngoingCheckingStatus;
  questionnarie?: COFAndIndividualQuestionnarieFormValues;
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  riskScore?: CustomerScore;
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  lastCalculatingAml?: CustomerScore;
  sourceOfWealth?: DictionaryItem[];
  sourceOfIncome?: DictionaryItem[];
  sourceOfWealthForIndividual?: DictionaryItem[];
  accountsCurrencies?: AccountsCurrencies[];
  questionnarieId?: string;
  accountsFlows?: AccountsFlows[];
  expectedFundsOrigins?: ExpectedFundsOrigins[];
  customerSourceOfFunds?: DictionaryItem[];
  crsAndFatca?: CRSAndFatca[];
  allowedDocumentsCategories?: AllowedCategory[];
}

const initialState: NewApplicationIndividualInfoState = {
  isLoading: false,
  requestInProgress: false,
  selectedDocumentsCounts: 0,
  hintVisible: false,
};

export const newAppliationIndividualInfoSlice = createSlice({
  name: 'newAppliationIndividualInfo',
  initialState,
  reducers: {
    onGroupSelect: (state, action: PayloadAction<boolean>) => {
      const nextDocuments = state.documents?.map((item) => {
        const result = { ...item };
        result.isSelected = action.payload;

        return result;
      });

      state.documents = nextDocuments;

      if (action.payload) {
        state.selectedDocumentsCounts = nextDocuments?.length as number;
      } else {
        state.selectedDocumentsCounts = 0;
      }
    },
    setSelected: (
      state,
      action: PayloadAction<{ id: string; selected: boolean }>,
    ) => {
      let count = state.selectedDocumentsCounts;

      const nextDocuments = state.documents?.map((item) => {
        const result = { ...item };

        if (action.payload.id === result.id) {
          if (action.payload.selected) {
            count += 1;
          } else {
            count -= 1;
          }

          result.isSelected = action.payload.selected;
        }

        return result;
      });

      state.documents = nextDocuments;
      state.selectedDocumentsCounts = count;
    },
    updateStepsStatus: (
      state,
      action: PayloadAction<NewApplicationIndividualStepsStatuses>,
    ) => {
      state.stepsStatus = action.payload;
    },
    setMatchSelected: (
      state,
      action: PayloadAction<{ id: string; selected: boolean }>,
    ) => {
      const nextInfo = { ...state.newApplicationInfo };

      nextInfo.similarApplications = {
        edges: nextInfo.similarApplications?.edges.map((item) => {
          const result = { ...item };
          if (result.node.id === action.payload.id) {
            result.node.selected = action.payload.selected;
          }

          return result;
        }) as { node: Match }[],
      };

      state.newApplicationInfo = nextInfo as NewApplicationListItem;
    },
    saveChanges: (state, action: PayloadAction<FormValues>) => {
      state.requestInProgress = true;

      const nextInfo = updateApplicationValues({
        values: action.payload,
        info: state.newApplicationInfo as NewApplicationListItem,
      });

      state.newApplicationInfo = nextInfo as NewApplicationListItem;
    },
    checkChatMessages: (state) => {
      state.newChatMessageRecieved = true;
      state.chatMessageHintIsVisible = true;
    },
    rejectApplication: (state) => {
      const nextInfo = { ...state.newApplicationInfo };

      nextInfo.status = NewApplicationStatus.DECLINED;

      state.newApplicationInfo = nextInfo as NewApplicationListItem;
    },
    closeChatMessageHint: (state) => {
      state.chatMessageHintIsVisible = false;
    },
    setHintVisible: (state, action: PayloadAction<boolean>) => {
      state.hintVisible = action.payload;
    },
    onSelectAllGroup: (state, action: PayloadAction<boolean>) => {
      const nextDocuments = state.documents?.map((item) => {
        const result = { ...item };

        if (!item.documentCategoryId?.includes('identification')) {
          result.isSelected = action.payload;
        }

        return result;
      }) as ApplicationDocument[];

      state.documents = nextDocuments;
      state.selectedDocumentsCounts = action.payload ? nextDocuments.length : 0;
    },
    onSelectDocument: (
      state,
      action: PayloadAction<{
        selected: boolean;
        id: string;
      }>,
    ) => {
      let selectedCount = state.selectedDocumentsCounts;

      if (state.documents) {
        const nextSelectedDocuments = state.documents?.map((item) => {
          const result = { ...item };

          if (item.id === action.payload.id) {
            result.isSelected = action.payload.selected;

            if (action.payload.selected) {
              selectedCount += 1;
            } else {
              selectedCount -= 1;
            }
          }

          return result;
        });

        state.documents = nextSelectedDocuments;
        state.selectedDocumentsCounts = selectedCount;
      }
    },
    deleteIsSuccess: (state, action: PayloadAction<ApplicationDocument[]>) => {
      state.documents = action.payload;
      state.selectedDocumentsCounts = 0;
    },
    setAllowedDocumentsCategories: (
      state,
      action: PayloadAction<AllowedCategory[]>,
    ) => {
      state.allowedDocumentsCategories = action.payload;
    },
    onDocumentMove: (state, action: PayloadAction<string>) => {
      const nextDocuments = state.documents?.map((item) => {
        const result = { ...item };

        if (result.isSelected) {
          result.isSelected = false;
          result.documentCategoryId = action.payload;
        }

        if (state.document && result.id === state.document.id) {
          result.documentCategoryId = action.payload;
        }

        return result;
      });

      state.documents = nextDocuments;

      if (state.document) {
        const nextDocument = { ...state.document };
        nextDocument.documentCategoryId = action.payload;

        state.document = nextDocument;
      }

      state.selectedDocumentsCounts = 0;
    },
    clearNewApplicationInfoState: (state) => {
      state.isLoading = false;
      state.applicationId = undefined;
      state.newApplicationInfo = undefined;
      state.chatMessageHintIsVisible = undefined;
      state.newChatMessageRecieved = undefined;
      state.selectedDocumentsCounts = 0;
      state.agreements = undefined;
      state.agreementsErrorsList = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
      state.ongoingMonitoring = undefined;
      state.amlUpdateStatus = undefined;
      state.riskScore = undefined;
      state.lastCalculatingAml = undefined;
      state.questionnarie = undefined;
      state.accountsCurrencies = undefined;
      state.accountsFlows = undefined;
      state.expectedFundsOrigins = undefined;
      state.crsAndFatca = undefined;
      state.questionnarieId = undefined;
      state.allowedDocumentsCategories = undefined;
    },
    clearDocumentSidepanelState: (state) => {
      state.documentId = undefined;
      state.document = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      applicationsApi.endpoints.getNewApplicationInfo.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        const { data } = action.payload;

        state.applicationId = action.payload.data.id;

        const similarApplications = data?.similarApplications?.edges.map(
          (item) => ({
            node: {
              id: item.node.id,
              firstName: item.node.firstName || '',
              lastName: item.node.lastName || '',
              status: item.node.status,
              userId: item.node.userId || '',
              isClient: item.node.isClient,
              type: item.node.type,
              companyName: item.node.companyName || '',
              selected: Boolean(
                data?.reviewedDuplicates?.find(
                  (duplicate) => item.node.id === duplicate,
                ),
              ),
            },
          }),
        );

        state.newApplicationInfo = {
          ...data,
          similarApplications: {
            edges: similarApplications as { node: Match }[],
          },
        };
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationLastCalculatingScore
        .matchFulfilled,
      (state, action: PayloadAction<Response<CustomerScore>>) => {
        state.lastCalculatingAml = action.payload.data.edges[0].node;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCustomerSourceOfWealth.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.sourceOfWealth = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCustomerSourceOfFunds.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.customerSourceOfFunds = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getSourceOfWealthForIndividualDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.sourceOfWealthForIndividual = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getSourceOfIncomeDictionary.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.sourceOfIncome = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateNewApplication.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        const { data } = action.payload;

        const similarApplications = data?.similarApplications?.edges.map(
          (item) => ({
            node: {
              id: item.node.id,
              firstName: item.node.firstName || '',
              lastName: item.node.lastName || '',
              status: item.node.status,
              userId: item.node.userId || '',
              isClient: item.node.isClient,
              type: item.node.type,
              companyName: item.node.companyName || '',
              selected: Boolean(
                data?.reviewedDuplicates?.find(
                  (duplicate) => item.node.id === duplicate,
                ),
              ),
            },
          }),
        );

        state.newApplicationInfo = {
          ...data,
          similarApplications: {
            edges: similarApplications as { node: Match }[],
          },
        };
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getNewApplicationOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        if (state.newApplicationInfo) {
          const nextInfo = { ...state.newApplicationInfo };

          nextInfo.organizationId = action.payload.data.organizationId;

          state.newApplicationInfo = nextInfo;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createApplicationRiskScore.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScoreQueuedProps }>) => {
        state.amlUpdateStatus = action.payload.data.status;
        localStorage.setItem('applicationAmlUpdateId', action.payload.data.id);
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationRiskScore.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScore }>) => {
        if (state.newApplicationInfo) {
          state.riskScore = action.payload.data;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateReviewStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        const { data } = action.payload;

        const nextNewApplicationInfo = { ...state.newApplicationInfo };

        nextNewApplicationInfo.reviewStatus = data.reviewStatus;

        state.newApplicationInfo =
          nextNewApplicationInfo as NewApplicationListItem;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getNewApplicationSystemLogs.matchFulfilled,
      (state, action: PayloadAction<Response<SystemLogs>>) => {
        state.systemLogs = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationChangeLogs.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getIndividualNewApplicationDocuments
        .matchFulfilled,
      (state, action: PayloadAction<Response<ApplicationDocument>>) => {
        state.documents = action.payload.data.edges
          .map((item) => item.node)
          ?.map((item) => {
            const result = { ...item };

            if (!result.documentCategoryId) {
              result.documentCategoryId = 'identification-category';
            }

            return result;
          });
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.submitApplication.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        if (state.newApplicationInfo) {
          state.newApplicationInfo = {
            ...state.newApplicationInfo,
            status: action.payload.data.status,
          };
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getIndividualNewApplicationDocument
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationDocument }>) => {
        const nextDocument = {
          ...action.payload.data,
          commentsList: action.payload.data.comments?.edges.map(
            (item) => item.node,
          ),
        };

        if (
          state.newApplicationInfo?.applicationScheme?.id ===
            'ed560559-8354-4634-b6dd-eb341dfb551a' &&
          !nextDocument.documentCategoryId
        ) {
          nextDocument.documentCategoryId = 'identification-documents';
        }
        state.document = nextDocument;

        state.documentId = action.payload.data.id;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.changeNewApplicationDocumentExpirationDate
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationDocument }>) => {
        if (state.document) {
          const nextDocument = { ...state?.document };

          nextDocument.expirationDate = action.payload.data.expirationDate;

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateIndividualApplicationStatus
        .matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        const { data } = action.payload;

        const nextNewApplicationInfo = { ...state.newApplicationInfo, ...data };

        state.newApplicationInfo = nextNewApplicationInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationAmlUpdatingItem.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScoreQueuedProps }>) => {
        state.amlUpdateStatus = action.payload.data.status;

        if (action.payload.data.status === CustomerScoreUpdateStatus.FAIL) {
          localStorage.removeItem('applicationAmlUpdateId');
        }

        if (action.payload.data.status === CustomerScoreUpdateStatus.SUCCESS) {
          localStorage.removeItem('applicationAmlUpdateId');
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.addNewApplicationComment.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationComment }>) => {
        const { data } = action.payload;

        if (state.newApplicationInfo) {
          const nextNewApplicationInfo = { ...state.newApplicationInfo };

          const nextComments = ([] as { node: ApplicationComment }[]).concat(
            nextNewApplicationInfo?.comments?.edges as {
              node: ApplicationComment;
            }[],
          );

          nextComments.push({ node: { ...data } });

          nextNewApplicationInfo.comments = { edges: nextComments };
          state.newApplicationInfo = nextNewApplicationInfo;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateNewApplicationComment.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationComment }>) => {
        const { data } = action.payload;

        if (state.newApplicationInfo) {
          const nextNewApplicationInfo = { ...state.newApplicationInfo };

          const nextComments = nextNewApplicationInfo?.comments?.edges.map(
            (item) => {
              let result = { ...item };

              if (result.node.id === data.id) {
                result = { node: data };
              }

              return result;
            },
          );

          nextNewApplicationInfo.comments = {
            edges: nextComments as { node: ApplicationComment }[],
          };

          state.newApplicationInfo = nextNewApplicationInfo;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteNewApplicationComment.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationComment }>) => {
        const { data } = action.payload;

        if (state.newApplicationInfo) {
          const nextNewApplicationInfo = { ...state.newApplicationInfo };

          const nextComments = nextNewApplicationInfo?.comments?.edges.filter(
            (item) => item.node.id !== data.id,
          );

          nextNewApplicationInfo.comments = {
            edges: nextComments as { node: ApplicationComment }[],
          };
          state.newApplicationInfo = nextNewApplicationInfo;
        }
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.addCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };
          let nextComments: DocumentComment[] = [];

          if (
            nextDocument.commentsList &&
            nextDocument.commentsList.length > 0
          ) {
            nextComments = ([] as DocumentComment[]).concat(
              nextDocument.commentsList,
            );
          }

          nextComments.unshift(action.payload.data);
          nextDocument.commentsList = nextComments;

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.editCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };
          nextDocument.commentsList = nextDocument.commentsList?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });

          const nextDocuments = state?.documents?.map((item) => {
            const result = { ...item };

            if (result.id === state?.document?.id) {
              result.countComments = {
                count: (result.countComments?.count || 0) + 1,
              };
            }

            return result;
          });

          state.documents = nextDocuments;
          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      clientsDocumentsApi.endpoints.deleteCustomersComment.matchFulfilled,
      (state, action: PayloadAction<{ data: DocumentComment }>) => {
        if (state.document) {
          const nextDocument = { ...state.document };

          nextDocument.commentsList = nextDocument?.commentsList?.filter(
            (item) => item.id !== action.payload.data.id,
          );

          const nextDocuments = state?.documents?.map((item) => {
            const result = { ...item };

            if (result.id === state?.document?.id) {
              result.countComments = {
                count: (result.countComments?.count as number) - 1,
              };
            }

            return result;
          });

          state.documents = nextDocuments;
          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationAgreements.matchPending,
      (state) => {
        state.agreementsErrorsList = undefined;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationAgreements.matchRejected,
      (state, action: any) => {
        state.agreements = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.agreementsErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.agreementsErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationAgreements.matchFulfilled,
      (state, action: PayloadAction<Response<ApplicationAgreement>>) => {
        state.agreements = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(
            ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_AGREEMENT,
            {
              applicationId: (state.applicationId as string) || '',
              agreementId: item.node.id,
            },
          ),
        }));
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.setReviewEmployee.matchFulfilled,
      (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
        const { data } = action.payload;

        const nextNewApplicationInfo = {
          ...state.newApplicationInfo,
          ...data,
        };

        state.newApplicationInfo = nextNewApplicationInfo;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationOngoingMonitoring.matchFulfilled,
      (state, action: PayloadAction<Response<OngoingMonitring>>) => {
        if (state.newApplicationInfo) {
          const items = action?.payload?.data?.edges?.map((item) => item.node);

          const sorted = items?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );

          state.ongoingMonitoring = [sorted[0]];
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.applyApplicationOngoingWarningAction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        const nextOngoingMonitoring = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          const nextWarnings = result?.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = { node: action.payload.data };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result?.warnings?.pageInfo,
          };

          return result;
        });

        state.ongoingMonitoring = nextOngoingMonitoring;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationOngoingWarning.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: TransactionWarning }> & {
          meta: {
            arg: {
              originalArgs: {
                id: string;
                warningId: string;
              };
            };
          };
        },
      ) => {
        if (state.newApplicationInfo) {
          const nextWarnings = state.ongoingMonitoring?.map((item) => {
            const result = { ...item };

            if (item.id === action.meta.arg.originalArgs.id) {
              const nextGroupWarnings = item.warnings?.edges?.map((warning) => {
                const warningResult = { ...warning };

                if (
                  warningResult.node.id ===
                  action.meta.arg.originalArgs.warningId
                ) {
                  warningResult.node.blacklistDoc =
                    action.payload.data.blacklistDoc;
                  warningResult.node.blacklistItem =
                    action.payload.data.blacklistItem;
                }

                return warningResult;
              });

              result.warnings = {
                edges: nextGroupWarnings,
                pageInfo: result.warnings.pageInfo,
              };
            }

            return result;
          });

          state.ongoingMonitoring = nextWarnings;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationWarningGroupWithPagination
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<TransactionWarning>> & {
          meta: {
            arg: {
              originalArgs: {
                warningGroupId: string;
              };
            };
          };
        },
      ) => {
        if (state.ongoingMonitoring) {
          const nextWarningsGroups = state.ongoingMonitoring?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs.warningGroupId) {
              const warningResult = { ...result.warnings };

              warningResult.pageInfo = action.payload.data.pageInfo;

              const currentWarnings = result.warnings.edges?.map(
                (warning) => warning.node,
              );

              const nextWarnings = ([] as TransactionWarning[]).concat(
                currentWarnings,
              );

              action?.payload?.data?.edges?.forEach((warning) =>
                nextWarnings.push(warning.node),
              );

              warningResult.edges = nextWarnings?.map((warning) => ({
                node: warning,
              }));

              result.warnings = warningResult;
            }

            return result;
          });

          state.ongoingMonitoring = nextWarningsGroups;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.resetApplicationWarningAction.matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        const nextOngoingMonitoring = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          const nextWarnings = result?.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = {
                node: {
                  ...action.payload.data,
                  action: undefined,
                  matchStatus: undefined,
                  comment: undefined,
                },
              };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result?.warnings?.pageInfo,
          };

          return result;
        });

        state.ongoingMonitoring = nextOngoingMonitoring;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.batchApplyApplicationOngoingWarningAction
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  action: string;
                  comment: string;
                  matchStatus?: string;
                };
              };
            };
          };
        },
      ) => {
        state.ongoingMonitoring = state.ongoingMonitoring?.map(
          (item: OngoingMonitring) => {
            const result = { ...item };

            const nextWarnings = result?.warnings?.edges?.map(
              (warning: { node: TransactionWarning }) => {
                let resultWarning = { ...warning };

                resultWarning = {
                  node: {
                    ...resultWarning.node,
                    action: action.meta.arg.originalArgs.body
                      .action as TransactionWarningActionEnum,
                    comment: action.meta.arg.originalArgs.body.comment,
                    matchStatus: action.meta.arg.originalArgs.body.matchStatus,
                  },
                };

                return resultWarning;
              },
            );

            result.warnings = {
              edges: nextWarnings,
              pageInfo: result?.warnings?.pageInfo,
            };

            return result;
          },
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationOngoingStatus.matchFulfilled,
      (state, action: PayloadAction<Response<OngoingCheckingStatus>>) => {
        if (state.newApplicationInfo) {
          const items = action?.payload?.data?.edges?.map((item) => item.node);

          const sorted = items?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );

          const [sortedValue] = sorted;

          state.ongoingCheckingStatus = sortedValue;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.restartOngoingChecking.matchFulfilled,
      (state, action: PayloadAction<{ data: OngoingCheckingStatus }>) => {
        if (state.newApplicationInfo) {
          state.ongoingCheckingStatus = action?.payload?.data;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getIndividualAccountsCurrencies.matchFulfilled,
      (state, action: PayloadAction<Response<AccountsCurrencies>>) => {
        state.accountsCurrencies = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createIndividualAccountsCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        let nextAccountsCurrencies: AccountsCurrencies[] = [];

        if (state.accountsCurrencies) {
          nextAccountsCurrencies = ([] as AccountsCurrencies[]).concat(
            state.accountsCurrencies,
          );
        }

        nextAccountsCurrencies.push(action.payload.data);

        state.accountsCurrencies = nextAccountsCurrencies;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateIndividualAccountsCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteIndividualAccountsCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<Response<CRSAndFatca>>) => {
        state.crsAndFatca = action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        let nextCRSAndFatca: CRSAndFatca[] = [];

        if (state.crsAndFatca) {
          nextCRSAndFatca = ([] as CRSAndFatca[]).concat(state.crsAndFatca);
        }

        nextCRSAndFatca.push(action.payload.data);

        state.crsAndFatca = nextCRSAndFatca;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        if (state.crsAndFatca) {
          state.crsAndFatca = state.crsAndFatca?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteCRSAndFacta.matchFulfilled,
      (state, action: PayloadAction<{ data: CRSAndFatca }>) => {
        if (state.crsAndFatca) {
          state.crsAndFatca = state.crsAndFatca?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getIndividualAccountsFlows.matchFulfilled,
      (state, action: PayloadAction<Response<AccountsFlows>>) => {
        state.accountsFlows = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createIndividualAccountsFlow.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        let nextAccountsFlows: AccountsFlows[] = [];

        if (state.accountsFlows) {
          nextAccountsFlows = ([] as AccountsFlows[]).concat(
            state.accountsFlows,
          );
        }

        nextAccountsFlows.push(action.payload.data);

        state.accountsFlows = nextAccountsFlows;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateIndividualAccountsFlow.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteIndividualAccountsFlow.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getIndividualExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<Response<ExpectedFundsOrigins>>) => {
        state.expectedFundsOrigins = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.createIndividualExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        let nextExpectedFundsOrigins: ExpectedFundsOrigins[] = [];

        if (state.expectedFundsOrigins) {
          nextExpectedFundsOrigins = ([] as ExpectedFundsOrigins[]).concat(
            state.expectedFundsOrigins,
          );
        }

        nextExpectedFundsOrigins.push(action.payload.data);

        state.expectedFundsOrigins = nextExpectedFundsOrigins;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateIndividualExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.map(
            (item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            },
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.deleteIndividualExptectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.getApplicationQuestionnarie.matchFulfilled,
      (state, action: PayloadAction<{ data: Questionnarie }>) => {
        if (state.newApplicationInfo && action?.payload?.data) {
          const { data } = action.payload;

          state.questionnarieId = action.payload.data.id;

          state.questionnarie = {
            nationalInsuranceNumberOfDirectors:
              data?.nationalInsuranceNumberOfDirectors || '',
            lastThreeYearsAddressHistory:
              data?.lastThreeYearsAddressHistory || '',
            criminalConvictedOfAny: data?.criminalConvictedOfAny?.toString()
              ? data?.criminalConvictedOfAny === false
                ? 'no'
                : 'yes'
              : '',
            criminalReceivedACautionForAny:
              data?.criminalReceivedACautionForAny?.toString()
                ? data?.criminalReceivedACautionForAny === false
                  ? 'no'
                  : 'yes'
                : '',
            criminalArrestedOrChargedWithOrBeenInvolved:
              data?.criminalArrestedOrChargedWithOrBeenInvolved?.toString()
                ? data?.criminalArrestedOrChargedWithOrBeenInvolved === false
                  ? 'no'
                  : 'yes'
                : '',
            criminalSubjectToAnyCurrentCriminalInvestigation:
              data?.criminalSubjectToAnyCurrentCriminalInvestigation?.toString()
                ? data?.criminalSubjectToAnyCurrentCriminalInvestigation ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted:
              data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?.toString()
                ? data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered:
              data.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString()
                ? data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved:
              data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?.toString()
                ? data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            civilFoundLiableForFraud: data?.civilFoundLiableForFraud?.toString()
              ? data?.civilFoundLiableForFraud === false
                ? 'no'
                : 'yes'
              : '',
            civilSubjectToAJudgmentDebt:
              data?.civilSubjectToAJudgmentDebt?.toString()
                ? data?.civilSubjectToAJudgmentDebt === false
                  ? 'no'
                  : 'yes'
                : '',
            civilInvolvedInAnyCivilProceedings:
              data?.civilInvolvedInAnyCivilProceedings?.toString()
                ? data?.civilInvolvedInAnyCivilProceedings === false
                  ? 'no'
                  : 'yes'
                : '',
            civilYouHeldAPositionOfInfluence:
              data?.civilYouHeldAPositionOfInfluence?.toString()
                ? data?.civilYouHeldAPositionOfInfluence === false
                  ? 'no'
                  : 'yes'
                : '',
            civilFiledForBankruptcy: data?.civilFiledForBankruptcy?.toString()
              ? data?.civilFiledForBankruptcy === false
                ? 'no'
                : 'yes'
              : '',
            civilSubjectToABankruptcy:
              data?.civilSubjectToABankruptcy?.toString()
                ? data?.civilSubjectToABankruptcy === false
                  ? 'no'
                  : 'yes'
                : '',
            businessAndEmploymentDismissedOrAskedTo:
              data?.businessAndEmploymentDismissedOrAskedTo?.toString()
                ? data?.businessAndEmploymentDismissedOrAskedTo === false
                  ? 'no'
                  : 'yes'
                : '',
            businessAndEmploymentDisqualifiedFromActingAsADirector:
              data?.businessAndEmploymentDisqualifiedFromActingAsADirector?.toString()
                ? data?.businessAndEmploymentDisqualifiedFromActingAsADirector ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings:
              data?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?.toString()
                ? data?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            regulatoryAnAuthorizationFromARegulatoryBodyRefused:
              data?.regulatoryAnAuthorizationFromARegulatoryBodyRefused?.toString()
                ? data?.regulatoryAnAuthorizationFromARegulatoryBodyRefused ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            regulatoryCensured: data?.regulatoryCensured?.toString()
              ? data?.regulatoryCensured === false
                ? 'no'
                : 'yes'
              : '',
            regulatoryInvolvedInAnyActivitiesRequiringAuthorization:
              data?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization?.toString()
                ? data?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            regulatorySubjectToAnyDisqualification:
              data?.regulatorySubjectToAnyDisqualification?.toString()
                ? data?.regulatorySubjectToAnyDisqualification === false
                  ? 'no'
                  : 'yes'
                : '',
            otherNationalitiesIds: data?.otherNationalitiesIds || [],
            maritalStatus: data?.maritalStatus || '',
            spouseFullName: data?.spouseFullName || '',
            natureOfBusinessActivity: data?.natureOfBusinessActivity || '',
            ownership: data?.ownership?.toString() || '',
            nameOfCompany: data?.nameOfCompany || '',
            belongsToGroup:
              data?.belongsToGroup?.toString() &&
              data?.belongsToGroup?.toString() !== 'null'
                ? data?.belongsToGroup === false
                  ? 'no'
                  : 'yes'
                : '',
            parentCompanyName: data?.parentCompanyName || '',
            parentCompanyCountryId: data?.parentCompanyCountryId || '',
            subsidiariesInfo: data?.subsidiariesInfo || '',
            mainActivities: data?.mainActivities || '',
            groupWealthSourceIds: data?.groupWealthSourceIds || [],
            groupWealthSourceOther: data?.groupWealthSourceOther || '',
            estimatedTotalWealthEuro:
              data?.estimatedTotalWealthEuro ||
              data?.estimatedTotalWealthEuro?.toString() === '0'
                ? data?.estimatedTotalWealthEuro?.toString()
                : '',
            requiresSourcesOfIncomes:
              data?.requiresSourcesOfIncomes?.toString() &&
              data?.requiresSourcesOfIncomes?.toString() !== 'null'
                ? data?.requiresSourcesOfIncomes === false
                  ? 'no'
                  : 'yes'
                : '',
            sourcesOfIncomes:
              (
                data?.sourcesOfIncomes as {
                  sourceId: string;
                  value: string;
                }[]
              )?.map((sourceOfIncome) => ({
                id: sourceOfIncome.sourceId,
                ...sourceOfIncome,
              })) || [],
            sourcesOfIncomesOther: data?.sourcesOfIncomesOther || '',
            sourcesOfIncomesBorrowedOther:
              data?.sourcesOfIncomesBorrowedOther || '',
            requiresSourceAndSizeOfWealth:
              data?.requiresSourceAndSizeOfWealth?.toString() &&
              data?.requiresSourceAndSizeOfWealth?.toString() !== 'null'
                ? data?.requiresSourceAndSizeOfWealth === false
                  ? 'no'
                  : 'yes'
                : '',
            sourcesAndSizesOfWealth:
              (
                data?.sourcesAndSizesOfWealth as {
                  sourceId: string;
                  value: string;
                }[]
              )?.map((sourceOfIncome) => ({
                id: sourceOfIncome.sourceId,
                ...sourceOfIncome,
              })) || [],
            sourcesAndSizesOfWealthOther:
              data?.sourcesAndSizesOfWealthOther || '',
            sourcesAndSizesOfWealthBorrowedOther:
              data?.sourcesAndSizesOfWealthBorrowedOther || '',
            requiresNetWorth:
              data?.requiresNetWorth?.toString() &&
              data?.requiresNetWorth?.toString() !== 'null'
                ? data?.requiresNetWorth === false
                  ? 'no'
                  : 'yes'
                : '',
            netWorthAmount: data?.netWorthAmount || '',
            sourcesOfFundsIds: data?.sourcesOfFundsIds || [],
            sourcesOfFundsLoanBusinessPartner:
              data?.sourcesOfFundsLoanBusinessPartner || '',
            sourcesOfFundsLoanCreditInstitution:
              data?.sourcesOfFundsLoanCreditInstitution || '',
            sourcesOfFundsOther: data?.sourcesOfFundsOther || '',
            secondPassportExpiryDate: data?.secondPassportExpiryDate || '',
            secondPassportIssueCountryId:
              data?.secondPassportIssueCountryId || '',
            secondPassportIssueDate: data?.secondPassportIssueDate || '',
            secondPassportNumber: data?.secondPassportNumber || '',
            isPepOrAssociate:
              data?.isPepOrAssociate?.toString() &&
              data?.isPepOrAssociate?.toString() !== 'null'
                ? data?.isPepOrAssociate === false
                  ? 'no'
                  : 'yes'
                : '',
            nameOfPep: data?.nameOfPep || '',
            relToPep: data?.relToPep || '',
            orgEmployerAndPosition: data?.orgEmployerAndPosition || '',
            positionHeldCountryId: data?.positionHeldCountryId || '',
            politicalFunctionPeriodFrom:
              data?.politicalFunctionPeriodFrom || '',
            politicalFunctionPeriodTo: data?.politicalFunctionPeriodTo || '',
            abilityMoveGovernmentFunds:
              data?.abilityMoveGovernmentFunds?.toString() &&
              data?.abilityMoveGovernmentFunds?.toString() !== 'null'
                ? data?.abilityMoveGovernmentFunds === false
                  ? 'no'
                  : 'yes'
                : '',
            abilityMoveGovernmentFundsDetails:
              data?.abilityMoveGovernmentFundsDetails || '',
            isUSCitizen:
              data?.isUSCitizen?.toString() &&
              data?.isUSCitizen?.toString() !== 'null'
                ? data?.isUSCitizen === false
                  ? 'no'
                  : 'yes'
                : '',
            homePhoneNumber: data?.homePhoneNumber || '',
            emails: data?.emails || [],
            hasAccountsInOtherFinancialInstitutions:
              data?.hasAccountsInOtherFinancialInstitutions?.toString() &&
              data?.hasAccountsInOtherFinancialInstitutions?.toString() !==
                'null'
                ? data?.hasAccountsInOtherFinancialInstitutions === false
                  ? 'no'
                  : 'yes'
                : '',
            otherFinancialInstitutions: data?.otherFinancialInstitutions || '',
          };
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.updateInividualQuestionnarie.matchFulfilled,
      (state, action: PayloadAction<{ data: Questionnarie }>) => {
        if (state.newApplicationInfo && action?.payload?.data) {
          const { data } = action.payload;

          state.questionnarie = {
            nationalInsuranceNumberOfDirectors:
              data?.nationalInsuranceNumberOfDirectors || '',
            lastThreeYearsAddressHistory:
              data?.lastThreeYearsAddressHistory || '',
            criminalConvictedOfAny: data?.criminalConvictedOfAny?.toString()
              ? data?.criminalConvictedOfAny === false
                ? 'no'
                : 'yes'
              : '',
            criminalReceivedACautionForAny:
              data?.criminalReceivedACautionForAny?.toString()
                ? data?.criminalReceivedACautionForAny === false
                  ? 'no'
                  : 'yes'
                : '',
            criminalArrestedOrChargedWithOrBeenInvolved:
              data?.criminalArrestedOrChargedWithOrBeenInvolved?.toString()
                ? data?.criminalArrestedOrChargedWithOrBeenInvolved === false
                  ? 'no'
                  : 'yes'
                : '',
            criminalSubjectToAnyCurrentCriminalInvestigation:
              data?.criminalSubjectToAnyCurrentCriminalInvestigation?.toString()
                ? data?.criminalSubjectToAnyCurrentCriminalInvestigation ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted:
              data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted?.toString()
                ? data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenConvicted ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered:
              data.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered?.toString()
                ? data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenOrdered ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved:
              data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved?.toString()
                ? data?.criminalFirmWhereYouHeldAPositionOfInfluenceEverBeenInvolved ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            civilFoundLiableForFraud: data?.civilFoundLiableForFraud?.toString()
              ? data?.civilFoundLiableForFraud === false
                ? 'no'
                : 'yes'
              : '',
            civilSubjectToAJudgmentDebt:
              data?.civilSubjectToAJudgmentDebt?.toString()
                ? data?.civilSubjectToAJudgmentDebt === false
                  ? 'no'
                  : 'yes'
                : '',
            civilInvolvedInAnyCivilProceedings:
              data?.civilInvolvedInAnyCivilProceedings?.toString()
                ? data?.civilInvolvedInAnyCivilProceedings === false
                  ? 'no'
                  : 'yes'
                : '',
            civilYouHeldAPositionOfInfluence:
              data?.civilYouHeldAPositionOfInfluence?.toString()
                ? data?.civilYouHeldAPositionOfInfluence === false
                  ? 'no'
                  : 'yes'
                : '',
            civilFiledForBankruptcy: data?.civilFiledForBankruptcy?.toString()
              ? data?.civilFiledForBankruptcy === false
                ? 'no'
                : 'yes'
              : '',
            civilSubjectToABankruptcy:
              data?.civilSubjectToABankruptcy?.toString()
                ? data?.civilSubjectToABankruptcy === false
                  ? 'no'
                  : 'yes'
                : '',
            businessAndEmploymentDismissedOrAskedTo:
              data?.businessAndEmploymentDismissedOrAskedTo?.toString()
                ? data?.businessAndEmploymentDismissedOrAskedTo === false
                  ? 'no'
                  : 'yes'
                : '',
            businessAndEmploymentDisqualifiedFromActingAsADirector:
              data?.businessAndEmploymentDisqualifiedFromActingAsADirector?.toString()
                ? data?.businessAndEmploymentDisqualifiedFromActingAsADirector ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings:
              data?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings?.toString()
                ? data?.businessAndEmploymentTheSubjectOfAnyDisciplinaryProceedings ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            regulatoryAnAuthorizationFromARegulatoryBodyRefused:
              data?.regulatoryAnAuthorizationFromARegulatoryBodyRefused?.toString()
                ? data?.regulatoryAnAuthorizationFromARegulatoryBodyRefused ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            regulatoryCensured: data?.regulatoryCensured?.toString()
              ? data?.regulatoryCensured === false
                ? 'no'
                : 'yes'
              : '',
            regulatoryInvolvedInAnyActivitiesRequiringAuthorization:
              data?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization?.toString()
                ? data?.regulatoryInvolvedInAnyActivitiesRequiringAuthorization ===
                  false
                  ? 'no'
                  : 'yes'
                : '',
            regulatorySubjectToAnyDisqualification:
              data?.regulatorySubjectToAnyDisqualification?.toString()
                ? data?.regulatorySubjectToAnyDisqualification === false
                  ? 'no'
                  : 'yes'
                : '',
            otherNationalitiesIds: data?.otherNationalitiesIds || [],
            maritalStatus: data?.maritalStatus || '',
            spouseFullName: data?.spouseFullName || '',
            natureOfBusinessActivity: data?.natureOfBusinessActivity || '',
            ownership: data?.ownership?.toString() || '',
            nameOfCompany: data?.nameOfCompany || '',
            belongsToGroup:
              data?.belongsToGroup?.toString() &&
              data?.belongsToGroup?.toString() !== 'null'
                ? data?.belongsToGroup === false
                  ? 'no'
                  : 'yes'
                : '',
            parentCompanyName: data?.parentCompanyName || '',
            parentCompanyCountryId: data?.parentCompanyCountryId || '',
            subsidiariesInfo: data?.subsidiariesInfo || '',
            mainActivities: data?.mainActivities || '',
            groupWealthSourceIds: data?.groupWealthSourceIds || [],
            groupWealthSourceOther: data?.groupWealthSourceOther || '',
            estimatedTotalWealthEuro:
              data?.estimatedTotalWealthEuro ||
              data?.estimatedTotalWealthEuro?.toString() === '0'
                ? data?.estimatedTotalWealthEuro?.toString()
                : '',
            requiresSourcesOfIncomes:
              data?.requiresSourcesOfIncomes?.toString() &&
              data?.requiresSourcesOfIncomes?.toString() !== 'null'
                ? data?.requiresSourcesOfIncomes === false
                  ? 'no'
                  : 'yes'
                : '',
            sourcesOfIncomes:
              (data?.sourcesOfIncomes as {
                sourceId: string;
                value: string;
              }[]) || [],
            sourcesOfIncomesOther: data?.sourcesOfIncomesOther || '',
            sourcesOfIncomesBorrowedOther:
              data?.sourcesOfIncomesBorrowedOther || '',
            requiresSourceAndSizeOfWealth:
              data?.requiresSourceAndSizeOfWealth?.toString() &&
              data?.requiresSourceAndSizeOfWealth?.toString() !== 'null'
                ? data?.requiresSourceAndSizeOfWealth === false
                  ? 'no'
                  : 'yes'
                : '',
            sourcesAndSizesOfWealth:
              (data?.sourcesAndSizesOfWealth as {
                sourceId: string;
                value: string;
              }[]) || [],
            sourcesAndSizesOfWealthOther:
              data?.sourcesAndSizesOfWealthOther || '',
            sourcesAndSizesOfWealthBorrowedOther:
              data?.sourcesAndSizesOfWealthBorrowedOther || '',
            requiresNetWorth:
              data?.requiresNetWorth?.toString() &&
              data?.requiresNetWorth?.toString() !== 'null'
                ? data?.requiresNetWorth === false
                  ? 'no'
                  : 'yes'
                : '',
            netWorthAmount: data?.netWorthAmount || '',
            sourcesOfFundsIds: data?.sourcesOfFundsIds || [],
            sourcesOfFundsLoanBusinessPartner:
              data?.sourcesOfFundsLoanBusinessPartner || '',
            sourcesOfFundsLoanCreditInstitution:
              data?.sourcesOfFundsLoanCreditInstitution || '',
            sourcesOfFundsOther: data?.sourcesOfFundsOther || '',
            secondPassportExpiryDate: data?.secondPassportExpiryDate || '',
            secondPassportIssueCountryId:
              data?.secondPassportIssueCountryId || '',
            secondPassportIssueDate: data?.secondPassportIssueDate || '',
            secondPassportNumber: data?.secondPassportNumber || '',
            isPepOrAssociate:
              data?.isPepOrAssociate?.toString() &&
              data?.isPepOrAssociate?.toString() !== 'null'
                ? data?.isPepOrAssociate === false
                  ? 'no'
                  : 'yes'
                : '',
            nameOfPep: data?.nameOfPep || '',
            relToPep: data?.relToPep || '',
            orgEmployerAndPosition: data?.orgEmployerAndPosition || '',
            positionHeldCountryId: data?.positionHeldCountryId || '',
            politicalFunctionPeriodFrom:
              data?.politicalFunctionPeriodFrom || '',
            politicalFunctionPeriodTo: data?.politicalFunctionPeriodTo || '',
            abilityMoveGovernmentFunds:
              data?.abilityMoveGovernmentFunds?.toString() &&
              data?.abilityMoveGovernmentFunds?.toString() !== 'null'
                ? data?.abilityMoveGovernmentFunds === false
                  ? 'no'
                  : 'yes'
                : '',
            abilityMoveGovernmentFundsDetails:
              data?.abilityMoveGovernmentFundsDetails || '',
            isUSCitizen:
              data?.isUSCitizen?.toString() &&
              data?.isUSCitizen?.toString() !== 'null'
                ? data?.isUSCitizen === false
                  ? 'no'
                  : 'yes'
                : '',
            homePhoneNumber: data?.homePhoneNumber || '',
            emails: data?.emails || [],
            hasAccountsInOtherFinancialInstitutions:
              data?.hasAccountsInOtherFinancialInstitutions?.toString() &&
              data?.hasAccountsInOtherFinancialInstitutions?.toString() !==
                'null'
                ? data?.hasAccountsInOtherFinancialInstitutions === false
                  ? 'no'
                  : 'yes'
                : '',
            otherFinancialInstitutions: data?.otherFinancialInstitutions || '',
          };
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.renameIndividualDocument.matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationDocument }>) => {
        if (state.documents) {
          const nextDocuments = state.documents?.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.name = action.payload.data.name;
            }

            return result;
          });

          state.documents = nextDocuments;
        }

        if (state.document && state.document.id === action.payload.data.id) {
          const nextDocument = { ...state.document };

          nextDocument.name = action.payload.data.name;

          state.document = nextDocument;
        }
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.addIndividualApplicationDocuments
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ApplicationDocument }>) => {
        const nextDocuments = ([] as ApplicationDocument[]).concat(
          state.documents as ApplicationDocument[],
        );

        nextDocuments.push(action.payload.data);

        state.documents = nextDocuments;
      },
    );
    builder.addMatcher(
      applicationsApi.endpoints.changeIndividualApplicationDocumentStatus
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  ids: string[];
                  status: ApplicationDocumentStatus;
                };
              };
            };
          };
        },
      ) => {
        const { ids, status } = action.meta.arg.originalArgs.body;

        const nextDocuments = state?.documents?.map((item) => {
          const result = { ...item };
          const currentDocumentId = ids.find((id) => result.id === id);

          if (result.id === currentDocumentId) {
            result.status = status;
          }

          return result;
        });

        if (state.document) {
          const nextDocument = { ...state.document };

          if (ids.find((item) => item === nextDocument.id)) {
            nextDocument.status = status;

            state.document = nextDocument;
          }
        }

        state.documents = nextDocuments;
      },
    );
  },
});

export const {
  checkChatMessages,
  closeChatMessageHint,
  clearNewApplicationInfoState,
  saveChanges,
  rejectApplication,
  setHintVisible,
  setMatchSelected,
  onGroupSelect,
  setSelected,
  clearDocumentSidepanelState,
  updateStepsStatus,
  onSelectAllGroup,
  onSelectDocument,
  deleteIsSuccess,
  onDocumentMove,
  setAllowedDocumentsCategories,
} = newAppliationIndividualInfoSlice.actions;

export default newAppliationIndividualInfoSlice.reducer;
